import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';
import { ExternalLink, RotateCcw, Save, Undo2 } from 'lucide-react';
import { Alert, Badge, Button, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { ContentField } from '@/components/admin/ContentFields';
import { setIn } from '@site/content/path';
import { CONTENT_KEY, defaultContent, mergeContent } from '@site/content';
import { pageSchemas, pageSchemaByKey } from '@site/content/schema';

const MODEL = 'CmsContent';
const errorText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;

const parse = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

/**
 * Admin editor for website copy. Each page's content lives in one cms_content
 * row; this form is generated from site/content/schema.js and writes the whole
 * page document back, so the public site updates as soon as a save succeeds.
 */
export default function ContentEditor({ module = 'site' }) {
  const { fetchApi } = useAppApi();
  const { mutate } = useSWRConfig();
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeKey, setActiveKey] = useState(pageSchemas[0].key);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedKey, setSavedKey] = useState('');

  const load = useCallback(() => {
    setLoadError('');
    return fetchApi('crud', { model: MODEL, action: 'list', limit: 200 })
      .then((res) => {
        const list = Array.isArray(res) ? res : (res && res.data) || [];
        setRows(Object.fromEntries(list.map((row) => [row.page_key, row])));
      })
      .catch((ex) => setLoadError(errorText(ex)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const schema = pageSchemaByKey[activeKey];
  const row = rows[activeKey];
  const stored = useMemo(
    () => mergeContent(defaultContent[activeKey] || {}, row ? parse(row.content) : undefined),
    [activeKey, row],
  );
  const draft = drafts[activeKey];
  const content = draft || stored;

  const change = (path, value) => {
    setSavedKey('');
    setDrafts((all) => ({ ...all, [activeKey]: setIn(all[activeKey] || stored, path, value) }));
  };

  const discard = () => setDrafts(({ [activeKey]: _drop, ...rest }) => rest);
  const resetToDefaults = () => setDrafts((all) => ({ ...all, [activeKey]: structuredClone(defaultContent[activeKey] || {}) }));

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const json = JSON.stringify(content);
      if (row) await fetchApi('crud', { model: MODEL, action: 'update', where: { id: row.id }, data: { content: json } });
      else await fetchApi('crud', { model: MODEL, action: 'create', data: { page_key: activeKey, content: json } });
      discard();
      setSavedKey(activeKey);
      await load();
      await mutate(CONTENT_KEY);
    } catch (ex) {
      setSaveError(errorText(ex));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader text="Loading…" />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold">Page content</h1>
        <p className="text-sm text-[var(--app-muted,#6b7280)]">
          Edit the text, images and lists on each page of the website. Saved changes go live straight away.
        </p>
      </div>

      {loadError ? <Alert variant="error" title="Could not load page content">{loadError}</Alert> : null}

      <div className="grid gap-6 lg:grid-cols-[14rem_minmax(0,1fr)]">
        <nav aria-label="Pages" className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible">
          {pageSchemas.map((page) => {
            const active = page.key === activeKey;
            return (
              <button
                key={page.key}
                type="button"
                onClick={() => {
                  setActiveKey(page.key);
                  setSaveError('');
                }}
                aria-current={active ? 'page' : undefined}
                className={`flex shrink-0 items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${
                  active
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-[var(--app-border)] bg-[var(--app-surface)] hover:bg-ink-50'
                }`}
              >
                {page.label}
                {drafts[page.key] ? <Badge variant="warning">Unsaved</Badge> : null}
              </button>
            );
          })}
        </nav>

        <section aria-labelledby="editor-heading" className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 id="editor-heading" className="text-lg font-semibold">{schema.label}</h2>
              <p className="text-sm text-[var(--app-muted,#6b7280)]">{schema.description}</p>
            </div>
            <a
              href={schema.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
            >
              View page <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          {savedKey === activeKey ? <Alert variant="success">Saved. The website now shows these changes.</Alert> : null}
          {saveError ? <Alert variant="error" title="Could not save">{saveError}</Alert> : null}

          {schema.sections.map((section) => (
            <div key={section.title} className="space-y-4 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-5">
              <h3 className="text-base font-semibold">{section.title}</h3>
              {section.description ? <p className="-mt-2 text-sm text-[var(--app-muted,#6b7280)]">{section.description}</p> : null}
              {section.fields.map((field) => (
                <ContentField key={field.path} field={field} content={content} onChange={change} />
              ))}
            </div>
          ))}

          <Can module={module} action="update">
            <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] p-3 shadow-lg">
              <Button variant="ghost" leftIcon={<RotateCcw className="h-4 w-4" />} onClick={resetToDefaults}>
                Restore original text
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" leftIcon={<Undo2 className="h-4 w-4" />} disabled={!draft} onClick={discard}>
                  Discard changes
                </Button>
                <Button leftIcon={<Save className="h-4 w-4" />} loading={saving} disabled={!draft} onClick={save}>
                  Save changes
                </Button>
              </div>
            </div>
          </Can>
        </section>
      </div>
    </div>
  );
}
